import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { CloudArrowUp, FileText, Package } from "@phosphor-icons/react";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui";
import {
  BookCover,
  ChecklistRow,
  PressEyebrow,
  PressPanel,
  StatusPill,
} from "@/components/pullpress/PressVisuals";

type Plan = {
  readonly name: string;
  readonly price: string;
  readonly cadence: string;
  readonly description: string;
  readonly href: string;
  readonly cta: string;
  readonly preferred?: true;
  readonly features: readonly string[];
};

const PLANS: readonly Plan[] = [
  {
    name: "Self-serve",
    price: "$997",
    cadence: "one-time package",
    description: "Choose this when you have the material and want a finished manuscript you can read, revise, and approve.",
    href: "/signup?package=self-serve",
    cta: "Start my book",
    features: ["Bring the source.", "Read the manuscript.", "Review the proof.", "Keep the packet together."],
  },
  {
    name: "Editorial",
    price: "$1,997",
    cadence: "one-time package",
    description: "Choose this when the book also needs a human editor to improve the draft before readers see it.",
    href: "/signup?package=editorial",
    cta: "Add my editor",
    preferred: true,
    features: ["Use the full production path.", "Bring in a human editor.", "Review edits inside the proof.", "Keep the final packet organized."],
  },
];

export function PricingPage() {
  return (
    <AppShell variant="public">
      <section className="border-b border-rule">
        <div className="mx-auto grid max-w-[1260px] gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[0.86fr_1fr] lg:px-8">
          <div>
            <PressEyebrow>You are paying for movement.</PressEyebrow>
            <h1 className="mt-4 max-w-[620px] font-display text-[clamp(2.2rem,4.2vw,4rem)] font-semibold leading-[0.98] tracking-as-tight text-ink-1">
              Pay for a book that gets finished.
            </h1>
            <p className="mt-4 max-w-[560px] font-text text-base leading-as-read text-ink-2">
              The price is not for another writing tool. It is for the path from raw material to a manuscript, proof, and release packet.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link to="/signup?package=self-serve"><Button variant="walnut" size="lg">Start my book</Button></Link>
              <Link to="/contact?intent=sales">
                <Button type="button" variant="secondary" size="lg">Talk to us</Button>
              </Link>
            </div>
          </div>
          <PressPanel className="p-4">
            <div className="grid gap-4 lg:grid-cols-[120px_1fr]">
              <BookCover title="Field Notes" subtitle="A working book" tone="linen" className="w-28" />
              <div>
                <PressEyebrow>The cost buys control.</PressEyebrow>
                <h2 className="mt-3 font-display text-2xl font-semibold leading-tight text-ink-1">
                  The book stays on one path.
                </h2>
                <div className="mt-4">
                  <ChecklistRow label="The source is in." status="complete" />
                  <ChecklistRow label="The manuscript has shape." status="complete" />
                  <ChecklistRow label="The proof can be judged." status="complete" />
                  <ChecklistRow label="The packet is being prepared." status="warning" />
                  <ChecklistRow label="The store handoff is next." status="pending" />
                </div>
              </div>
            </div>
          </PressPanel>
        </div>
      </section>

      <section className="border-b border-rule bg-paper-raised">
        <div className="mx-auto max-w-[1260px] px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <PressEyebrow>Choose the amount of help.</PressEyebrow>
              <h2 className="mt-3 font-display text-[clamp(1.75rem,2.6vw,2.65rem)] font-semibold leading-tight text-ink-1">
                Buy the path. Add judgment when the book needs it.
              </h2>
            </div>
            <StatusPill tone="editing">One-time packages</StatusPill>
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {PLANS.map((plan) => (
              <PressPanel key={plan.name} className={`relative flex flex-col p-5 ${plan.preferred ? "border-walnut shadow-as-2" : ""}`}>
                {plan.preferred && <div className="absolute right-4 top-4"><StatusPill tone="editing">Popular</StatusPill></div>}
                <h3 className="font-display text-2xl font-semibold text-ink-1">{plan.name}</h3>
                <div className="mt-5">
                  <span className="font-display text-4xl font-semibold tracking-as-tight text-ink-1">{plan.price}</span>
                  <span className="ml-2 font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">{plan.cadence}</span>
                </div>
                <p className="mt-4 min-h-[76px] font-text text-sm leading-as-read text-ink-2">{plan.description}</p>
                <ul className="mt-5 flex-1 space-y-2">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 font-text text-sm leading-as-read text-ink-2">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-walnut" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to={plan.href} className="mt-5 block">
                  <Button variant={plan.preferred ? "walnut" : "secondary"} className="w-full">{plan.cta}</Button>
                </Link>
              </PressPanel>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-rule">
        <div className="mx-auto grid max-w-[1260px] gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[0.82fr_1.18fr] lg:px-8">
          <div>
            <PressEyebrow>What you leave with.</PressEyebrow>
            <h2 className="mt-3 font-display text-2xl font-semibold leading-tight text-ink-1">
              PullPress prepares the book packet you need.
            </h2>
            <p className="mt-3 font-text text-base leading-as-read text-ink-2">
              You still control your store account, ISBN, upload settings, and platform approval. PullPress keeps the book files ready for that handoff.
            </p>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <ScopeTile icon={<CloudArrowUp size={22} />} title="Bring the source." body="Use the material you already have for one book." />
            <ScopeTile icon={<FileText size={22} />} title="Judge the proof." body="See what is ready and what still needs a decision." />
            <ScopeTile icon={<Package size={22} />} title="Keep the packet." body="Keep files and book details ready for handoff." />
          </div>
        </div>
      </section>
    </AppShell>
  );
}

function ScopeTile({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <PressPanel className="p-4">
      <div className="text-walnut">{icon}</div>
      <h3 className="mt-3 font-display text-lg font-semibold leading-tight text-ink-1">{title}</h3>
      <p className="mt-2 font-text text-sm leading-as-read text-ink-2">{body}</p>
    </PressPanel>
  );
}
